import type { HttpClient } from "@/shared/api/http";
import { isApiClientError } from "@/shared/api/errors";
import { renderPrintDocument, type RenderPrintRequest } from "@/modules/print/api/printApi";
import { printHtmlDocument } from "@/platform/printDocument";
import { saveBlobAsFile } from "@/platform/saveFile";

export type ExportPrintPdfRequest = Pick<
  RenderPrintRequest,
  "docCode" | "entityKey" | "entityType"
>;

/**
 * BE sync PDF (QuestPDF) → authenticated download via `downloadPath`.
 * Web: browser download. Native: cache + system share sheet (`saveBlobAsFile`).
 * Falls back to HTML print only if download path is missing.
 */
export async function exportPrintPdf(
  http: HttpClient,
  request: ExportPrintPdfRequest,
): Promise<void> {
  let res;
  try {
    res = await renderPrintDocument(http, {
      docCode: request.docCode,
      entityKey: request.entityKey,
      entityType: request.entityType,
      format: "pdf",
      archive: true,
    });
  } catch (e) {
    throw new Error(exportPdfErrorMessage(e));
  }

  const path = res.downloadPath;
  if (path) {
    try {
      const blob = await http.requestBlob(path);
      const name = res.fileName ?? `${request.docCode}-${request.entityKey}.pdf`;
      await saveBlobAsFile(blob, name);
      return;
    } catch (e) {
      throw new Error(exportPdfErrorMessage(e));
    }
  }

  // Fallback — print HTML (legacy / misconfigured BE). Native uses system print sheet.
  if (res.html) {
    await printHtmlDocument(
      res.html,
      res.fileName ?? `${request.docCode}-${request.entityKey}`,
    );
    return;
  }

  throw new Error("BE không trả downloadPath PDF.");
}

export function exportPdfErrorMessage(e: unknown): string {
  if (isApiClientError(e)) return e.message;
  if (e instanceof Error && e.message) return e.message;
  return "Xuất PDF thất bại";
}
